// ============================================================
// PlatformBillingSummaryPage.js — the billing summary (ruled 2026-08-21).
// Headcounts + module activations per client: the licence counts a
// month's invoices are based on.
//
// Two views through one selector:
//   - LIVE: current state across every ACTIVE NON-DEMO client — including
//     clients not yet set up for billing, flagged, because "how many
//     licences need to be included" must not depend on a toggle.
//   - a captured month-end SNAPSHOT (the 1st-of-month timer's record;
//     month-end counting, no proration — ruled). Snapshots are frozen:
//     a later rate or headcount change never rewrites a past month.
//
// Capture is idempotent (re-capture updates the period), so the button
// needs no confirm — nothing destructive, nothing external.
// ============================================================

import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import { toast } from '../components/Toaster';
import {
  getBillingSummary, listBillingSnapshots, getBillingSnapshot,
  downloadBillingSnapshotPdf, captureBillingSnapshot,
} from '../api/billingSummary';
import { poundsFromPence } from '../api/billing';

const currentPeriod = () => new Date().toISOString().slice(0, 7);
const LIVE = 'live';

export default function PlatformBillingSummaryPage() {
  const [view, setView] = useState(LIVE);
  const [periods, setPeriods] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadPeriods = useCallback(async () => {
    try {
      const res = await listBillingSnapshots();
      setPeriods(res.periods || []);
    } catch { /* the picker degrades to Live-only; the view itself still loads */ }
  }, []);

  const loadView = useCallback(async (which) => {
    setLoading(true); setError(null); setData(null);
    try {
      const res = which === LIVE ? await getBillingSummary() : await getBillingSnapshot(which);
      setData(res);
    } catch (err) {
      setError(err?.detail || err?.message || 'Failed to load the billing summary.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPeriods(); }, [loadPeriods]);
  useEffect(() => { loadView(view); }, [view, loadView]);

  async function capture() {
    // Live view captures the CURRENT month; a snapshot view re-captures its
    // own period (the recovery path for a failed timer run).
    const period = view === LIVE ? currentPeriod() : view;
    setBusy(true);
    try {
      const res = await captureBillingSnapshot(period);
      toast.success(`Snapshot captured for ${res.period} (${res.capturedCount} client${res.capturedCount === 1 ? '' : 's'}).`);
      await loadPeriods();
      setView(res.period); // land on what was just captured
    } catch (err) {
      toast.error(err?.detail || err?.message || 'Capture failed.');
    } finally {
      setBusy(false);
    }
  }

  async function downloadPdf() {
    setBusy(true);
    try {
      const blob = await downloadBillingSnapshotPdf(view);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `carehub-billing-summary-${view}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.detail || err?.message || 'Download failed.');
    } finally {
      setBusy(false);
    }
  }

  const clients = (data && data.clients) || [];
  const totals = (data && data.totals) || { managerCount: 0, adminCount: 0, carerCount: 0, auditorCount: 0 };
  const isSnapshot = view !== LIVE;
  const capturedAt = isSnapshot && clients.length > 0 ? clients[0].capturedAt : null;

  const columns = [
    {
      key: 'clientName', label: 'Client', mobile: 'title',
      render: (r) => (
        <>
          <Link to={`/clients/${r.clientId}?tab=billing`}>{r.clientName}</Link>
          {r.lifecycleStatus === 'trial_active' ? <span style={{ marginLeft: 6, color: 'var(--color-text-muted, #555)', fontSize: '0.85em' }}>trial</span> : null}
        </>
      ),
    },
    { key: 'managerCount', label: 'Managers', width: '90px', align: 'right', render: (r) => r.managerCount, mobileLabel: 'Managers' },
    { key: 'adminCount', label: 'Admins', width: '80px', align: 'right', render: (r) => r.adminCount, mobileLabel: 'Admins' },
    { key: 'carerCount', label: 'Carers', width: '80px', align: 'right', render: (r) => r.carerCount, mobileLabel: 'Carers' },
    { key: 'auditorCount', label: 'Auditors', width: '85px', align: 'right', render: (r) => r.auditorCount, mobile: 'hide' },
    {
      key: 'modules', label: 'Active modules', mobile: 'meta', mobileLabel: 'Modules',
      render: (r) => ((r.modules || []).length === 0
        ? '—'
        : r.modules.map((m) => `${m.moduleName || m.moduleKey}${m.grantSource === 'dependency' ? ' (included)' : ''}`).join(', ')),
    },
    {
      key: 'totalPence', label: 'Monthly total', width: '130px', align: 'right', mobile: 'badge',
      render: (r) => (r.billingEnabled
        ? <strong>£{poundsFromPence(r.totalPence)}</strong>
        : <span style={{ color: 'var(--color-text-muted, #555)' }}>Not configured</span>),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Billing Summary"
        subtitle="Headcounts and module activations per client - the licence counts each month's invoices are based on. Month-end counting: whoever is active when the snapshot is captured is counted for that month."
      />

      <div style={{ display: 'flex', gap: 'var(--space-3, 12px)', flexWrap: 'wrap', alignItems: 'center', margin: '0 0 12px' }}>
        <select aria-label="Period" value={view} onChange={(e) => setView(e.target.value)}>
          <option value={LIVE}>{`Live (${currentPeriod()})`}</option>
          {periods.map((p) => <option key={p.period} value={p.period}>{`Snapshot ${p.period}`}</option>)}
        </select>
        <button className="button-secondary" disabled={busy || loading} onClick={capture}>
          {isSnapshot ? `Re-capture ${view}` : `Capture snapshot (${currentPeriod()})`}
        </button>
        {isSnapshot && (
          <button className="button-secondary" disabled={busy || loading} onClick={downloadPdf}>Download PDF</button>
        )}
        {capturedAt && (
          <span style={{ color: 'var(--color-text-muted, #555)', fontSize: '0.9em' }}>
            Captured {new Date(capturedAt).toLocaleString('en-GB', { timeZone: 'UTC' })} UTC
          </span>
        )}
      </div>

      {data && (
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', margin: '0 0 12px' }}>
          {[
            ['Clients', `${data.clientCount} (${data.billingEnabledCount} billing-enabled)`],
            ['Managers (NI/RM)', totals.managerCount],
            ['Admins', totals.adminCount],
            ['Carers', totals.carerCount],
            ['Auditors', totals.auditorCount],
            ['Grand total', `£${poundsFromPence(data.grandTotalPence)}`],
          ].map(([label, value]) => (
            <div key={label} style={{ background: 'var(--color-surface, #fff)', border: '1px solid var(--color-border, #DEE2E6)', borderRadius: 8, padding: '8px 14px' }}>
              <div style={{ fontSize: '0.8em', color: 'var(--color-text-muted, #555)' }}>{label}</div>
              <div style={{ fontWeight: 700 }}>{value}</div>
            </div>
          ))}
        </div>
      )}

      <DataTable
        data={clients}
        columns={columns}
        rowKey={(r) => r.clientId}
        loading={loading}
        error={error}
        onRetry={() => loadView(view)}
        emptyMessage={isSnapshot ? 'No snapshot rows for this period.' : 'No active clients.'}
      />
    </div>
  );
}
